import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LifeBuoy, Send, Clock, CheckCircle2, XCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { submitRestorationTicket, listRestorationTickets } from "../lib/api";
import { useT } from "../lib/i18n";

const STATUS_META = {
  pending:   { icon: Clock,        cls: "text-amber-600 bg-amber-50 border-amber-200" },
  in_review: { icon: Search,       cls: "text-sky-600 bg-sky-50 border-sky-200" },
  resolved:  { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  rejected:  { icon: XCircle,      cls: "text-red-600 bg-red-50 border-red-200" },
};

export default function PurchaseRestore() {
  const { user, login } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const t = (en, ms) => (lang === "en" ? en : ms);
  const [provider, setProvider] = useState("stripe");
  const [purchaseEmail, setPurchaseEmail] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [receiptHint, setReceiptHint] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);

  const refresh = async () => {
    setLoadingTickets(true);
    try {
      const list = await listRestorationTickets();
      setTickets(list);
    } catch {} finally { setLoadingTickets(false); }
  };

  useEffect(() => {
    if (user) refresh();
    else setLoadingTickets(false);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { login(); return; }
    if (!message.trim()) { toast.error(t("Please describe your issue.", "Sila terangkan isu anda.")); return; }
    setBusy(true);
    try {
      await submitRestorationTicket({
        provider,
        purchase_email: purchaseEmail,
        transaction_id: transactionId,
        receipt_hint: receiptHint,
        message,
      });
      toast.success(t("Ticket submitted. We'll email you soon.", "Tiket dihantar. Kami akan e-mel anda tidak lama lagi."));
      setPurchaseEmail(""); setTransactionId(""); setReceiptHint(""); setMessage("");
      refresh();
    } catch (e) {
      toast.error(String(e.response?.data?.detail || e.message || "Error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E8E8ED]">
        <div className="max-w-2xl mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <Link to="/settings" className="p-2 -ml-2 rounded-full hover:bg-[#F5F5F7]" data-testid="restore-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#0A2540]" />
          </Link>
          <LifeBuoy className="w-4 h-4 text-[#4FB3E8]" />
          <div className="font-semibold text-[#0A2540]">{t("Purchase Restoration", "Pemulihan Pembelian")}</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-6 space-y-6">
        <section className="bg-white rounded-3xl p-5 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-[#E8E8ED]">
          <h1 className="text-xl font-semibold text-[#0A2540]">
            {t("Purchase linked to the wrong account?", "Pembelian terikat pada akaun yang salah?")}
          </h1>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">
            {t(
              "Tell us what happened. Our team will verify and move your Full Access entitlement to your current account.",
              "Beritahu kami apa yang berlaku. Pasukan kami akan sahkan dan pindahkan Full Access ke akaun semasa anda."
            )}
          </p>

          {!user && (
            <div className="mt-4 p-3 rounded-2xl bg-[#F5F5F7] flex items-center justify-between">
              <div className="text-sm text-gray-600">{t("Please sign in first.", "Sila log masuk dahulu.")}</div>
              <button onClick={login} data-testid="restore-login-btn" className="chip bg-[#0A2540] text-white text-xs px-4 py-1.5 rounded-full">
                {t("Sign In", "Log Masuk")}
              </button>
            </div>
          )}

          {user && (
            <form onSubmit={submit} className="space-y-3 mt-5" data-testid="restore-form">
              <div>
                <label className="text-xs text-gray-500">{t("Provider", "Penyedia")}</label>
                <div className="mt-1 inline-flex rounded-full bg-[#F5F5F7] p-1">
                  <button type="button" data-testid="provider-stripe" onClick={() => setProvider("stripe")} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${provider === "stripe" ? "bg-white text-[#0A2540] shadow" : "text-gray-500"}`}>Stripe (Web)</button>
                  <button type="button" data-testid="provider-apple" onClick={() => setProvider("apple")} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${provider === "apple" ? "bg-white text-[#0A2540] shadow" : "text-gray-500"}`}>Apple (iOS)</button>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">{t("Email used at purchase (if different)", "E-mel yang digunakan semasa membeli (jika berbeza)")}</label>
                <input data-testid="restore-purchase-email" value={purchaseEmail} onChange={(e) => setPurchaseEmail(e.target.value)} placeholder="old@email.com" className="w-full mt-1 px-4 py-2.5 rounded-full bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">{provider === "stripe" ? t("Stripe receipt / session ID", "ID resit / sesi Stripe") : t("Apple order ID (original_transaction_id)", "ID pesanan Apple (original_transaction_id)")}</label>
                <input data-testid="restore-txn-id" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder={provider === "stripe" ? "cs_… or pi_…" : "1000000123456789"} className="w-full mt-1 px-4 py-2.5 rounded-full bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t("Receipt number / order date hint (optional)", "Nombor resit / tarikh belian (jika ada)")}</label>
                <input data-testid="restore-receipt-hint" value={receiptHint} onChange={(e) => setReceiptHint(e.target.value)} placeholder={t("e.g. 2026-02-14, RM199", "cth. 2026-02-14, RM199")} className="w-full mt-1 px-4 py-2.5 rounded-full bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t("Explain what happened", "Terangkan apa yang berlaku")}</label>
                <textarea data-testid="restore-message" value={message} onChange={(e) => setMessage(e.target.value)} required rows={4} className="w-full mt-1 px-4 py-3 rounded-2xl bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm resize-none" placeholder={t("I bought Full Access under old@email.com but I use new@email.com now…", "Saya membeli Full Access di old@email.com tetapi kini saya guna new@email.com…")} />
              </div>
              <button
                data-testid="restore-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full py-3 rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
                {busy ? t("Submitting…", "Menghantar…") : t("Submit Request", "Hantar Permintaan")}
              </button>
              <p className="text-[11px] text-gray-400 text-center">
                {t("Our team responds within 2 business days via support@tripstera.app.", "Pasukan kami akan balas dalam 2 hari bekerja melalui support@tripstera.app.")}
              </p>
            </form>
          )}
        </section>

        {user && (
          <section className="bg-white rounded-3xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-[#E8E8ED]">
            <div className="text-xs uppercase tracking-[0.15em] text-gray-400 mb-3">{t("Your tickets", "Tiket anda")}</div>
            {loadingTickets && <div className="text-sm text-gray-400">{t("Loading…", "Memuat…")}</div>}
            {!loadingTickets && tickets.length === 0 && (
              <div className="text-sm text-gray-500">{t("No tickets yet.", "Tiada tiket lagi.")}</div>
            )}
            <div className="space-y-3">
              {tickets.map((tk) => {
                const meta = STATUS_META[tk.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <div key={tk.ticket_id} data-testid={`ticket-${tk.ticket_id}`} className="p-3 rounded-2xl border border-[#F5F5F7] hover:bg-[#FBFBFD] transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`}>
                            <Icon className="w-3 h-3" /> {tk.status.replace("_", " ")}
                          </span>
                          <span className="text-[11px] text-gray-400">
                            {new Date(tk.created_at).toLocaleDateString()}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5F5F7] text-gray-500">{tk.provider}</span>
                        </div>
                        <div className="text-sm text-[#1D1D1F] mt-1 truncate">{tk.message}</div>
                        {tk.transaction_id && (
                          <div className="text-[11px] text-gray-400 mt-0.5 truncate">Ref: {tk.transaction_id}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
